import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Database, Search, User, Activity, RefreshCw } from 'lucide-react';
import Button from './ui/Button';
import { inmateService } from '../services/inmateService';







export default function DatabaseSearchDiagnosticPanel() {
  const [diagnostics, setDiagnostics] = useState({
    databaseConnection: null,
    searchFunctions: null,
    joseJimenezSearch: null,
    testData: null
  });
  const [isRunning, setIsRunning] = useState(false);
  const [currentTest, setCurrentTest] = useState('');

  // Comprehensive diagnostic runner
  const runDiagnostics = async () => {
    setIsRunning(true);
    setCurrentTest('Initializing diagnostics...');
    
    const results = {
      databaseConnection: null,
      searchFunctions: null,
      joseJimenezSearch: null,
      testData: null
    };

    try {
      // Test 1: Database Connectivity
      setCurrentTest('Testing database connection...');
      try {
        const connectionResult = await inmateService?.testDatabaseConnection();
        results.databaseConnection = {
          success: connectionResult?.success,
          message: connectionResult?.message || connectionResult?.error,
          details: connectionResult?.tests
        };
      } catch (error) {
        results.databaseConnection = {
          success: false,
          message: `Connection test failed: ${error?.message}`,
          details: null
        };
      }

      // Test 2: Jose Jiminez Specific Search
      setCurrentTest('Testing Jose Jiminez search...');
      try {
        const joseSearch = await inmateService?.flexibleSearch({
          firstName: 'Jose',
          lastName: 'Jiminez',
          fuzzy: true,
          page: 1,
          pageSize: 10
        });
        
        results.joseJimenezSearch = {
          success: joseSearch?.success,
          message: joseSearch?.success 
            ? `Found ${joseSearch?.data?.length || 0} records for Jose Jiminez`
            : `Search failed: ${joseSearch?.error}`,
          resultCount: joseSearch?.data?.length || 0,
          results: joseSearch?.data || []
        };
      } catch (error) {
        results.joseJimenezSearch = {
          success: false,
          message: `Jose Jiminez search failed: ${error?.message}`,
          resultCount: 0,
          results: []
        };
      }

      // Test 3: Enhanced Search Functions
      setCurrentTest('Testing enhanced search functions...');
      try {
        // Test fuzzy search with variations
        const variations = [
          { firstName: 'Jose', lastName: 'Jiminez' },
          { firstName: 'José', lastName: 'Jiménez' },
          { firstName: 'Joe', lastName: 'Jiminez' },
          { query: 'Jose Jiminez' }
        ];

        let totalFound = 0;
        const searchResults = [];

        for (const variation of variations) {
          const result = await inmateService?.flexibleSearch({
            ...variation,
            fuzzy: true,
            pageSize: 5
          });
          
          if (result?.success && result?.data?.length > 0) {
            totalFound += result?.data?.length;
            searchResults?.push({
              searchTerm: variation,
              count: result?.data?.length,
              records: result?.data
            });
          }
        }

        results.searchFunctions = {
          success: totalFound > 0,
          message: totalFound > 0 
            ? `Enhanced search found ${totalFound} total results across variations`
            : 'No results found with enhanced search variations',
          variations: searchResults?.length,
          totalResults: totalFound
        };
      } catch (error) {
        results.searchFunctions = {
          success: false,
          message: `Enhanced search test failed: ${error?.message}`,
          variations: 0,
          totalResults: 0
        };
      }

      // Test 4: Test Data Verification
      setCurrentTest('Verifying test data...');
      try {
        // Check if we have any persons data at all
        const generalSearch = await inmateService?.flexibleSearch({
          firstName: 'John',
          fuzzy: true,
          pageSize: 5
        });
        
        results.testData = {
          success: generalSearch?.success,
          message: generalSearch?.success 
            ? `Database contains ${generalSearch?.pagination?.total || generalSearch?.data?.length || 0} searchable records`
            : `Test data verification failed: ${generalSearch?.error}`,
          recordCount: generalSearch?.pagination?.total || generalSearch?.data?.length || 0
        };
      } catch (error) {
        results.testData = {
          success: false,
          message: `Test data verification failed: ${error?.message}`,
          recordCount: 0
        };
      }

    } catch (error) {
      console.error('🚨 Comprehensive diagnostics failed:', error);
    }

    setDiagnostics(results);
    setIsRunning(false);
    setCurrentTest('');
  };

  // Auto-run diagnostics on component mount
  useEffect(() => {
    runDiagnostics();
  }, []);

  const DiagnosticResult = ({ title, result, icon: IconComponent }) => {
    if (!result) return null;

    return (
      <div className="border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
          <IconComponent className="w-5 h-5" />
          <h3 className="font-medium">{title}</h3>
          {result?.success ? (
            <CheckCircle className="w-5 h-5 text-green-500" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-500" />
          )}
        </div>
        <div className={`p-3 rounded text-sm ${
          result?.success 
            ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <p className="font-medium">
            {result?.success ? '✅ SUCCESS' : '❌ FAILURE'}
          </p>
          <p className="mt-1">{result?.message}</p>
          
          {/* Additional details for specific tests */}
          {result?.details && (
            <div className="mt-2 text-xs">
              <p>Details:</p>
              <pre className="mt-1 whitespace-pre-wrap">
                {JSON.stringify(result?.details, null, 2)}
              </pre>
            </div>
          )}
          
          {result?.resultCount !== undefined && (
            <p className="mt-1 font-medium">Results found: {result?.resultCount}</p>
          )}
          
          {result?.results && result?.results?.length > 0 && (
            <div className="mt-2">
              <p className="font-medium">Sample results:</p>
              {result?.results?.slice(0, 3)?.map((person, index) => (
                <div key={index} className="mt-1 text-xs bg-white p-2 rounded border">
                  <p><strong>Name:</strong> {person?.first_name} {person?.middle_name || ''} {person?.last_name}</p>
                  {person?.dob && <p><strong>DOB:</strong> {person?.dob}</p>}
                  {person?.current_status && <p><strong>Status:</strong> {person?.current_status}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const getOverallStatus = () => {
    const results = Object.values(diagnostics)?.filter(r => r !== null);
    if (results?.length === 0) return 'pending';
    
    const successCount = results?.filter(r => r?.success)?.length;
    if (successCount === results?.length) return 'success';
    if (successCount > 0) return 'warning';
    return 'error';
  };

  const overallStatus = getOverallStatus();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Database Search Diagnostics</h2>
          <p className="text-sm text-gray-600 mt-1">
            Comprehensive testing of database search functions and Jose Jiminez issue
          </p>
        </div>
        
        <Button
          onClick={runDiagnostics}
          disabled={isRunning}
          variant="outline"
          className="flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${isRunning ? 'animate-spin' : ''}`} />
          {isRunning ? 'Running...' : 'Run Diagnostics'}
        </Button>
      </div>
      {/* Overall Status */}
      <div className={`p-4 rounded-lg border ${
        overallStatus === 'success' ? 'bg-green-50 border-green-200' :
        overallStatus === 'warning' ? 'bg-yellow-50 border-yellow-200' :
        overallStatus === 'error' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-center gap-2">
          {overallStatus === 'success' && <CheckCircle className="w-5 h-5 text-green-500" />}
          {overallStatus === 'warning' && <AlertCircle className="w-5 h-5 text-yellow-500" />}
          {overallStatus === 'error' && <AlertCircle className="w-5 h-5 text-red-500" />}
          {overallStatus === 'pending' && <Activity className="w-5 h-5 text-gray-500" />}
          
          <h3 className="font-medium">
            {overallStatus === 'success' && 'All Systems Operational'}
            {overallStatus === 'warning' && 'Some Issues Detected'}
            {overallStatus === 'error' && 'Critical Issues Found'}
            {overallStatus === 'pending' && 'Running Diagnostics...'}
          </h3>
        </div>
        
        {currentTest && (
          <p className="text-sm text-gray-600 mt-1">
            Current test: {currentTest}
          </p>
        )}
      </div>
      {/* Individual Test Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DiagnosticResult 
          title="Database Connection"
          result={diagnostics?.databaseConnection}
          icon={Database}
        />
        
        <DiagnosticResult 
          title="Jose Jiminez Search"
          result={diagnostics?.joseJimenezSearch}
          icon={User}
        />
        
        <DiagnosticResult 
          title="Enhanced Search Functions"
          result={diagnostics?.searchFunctions}
          icon={Search}
        />
        
        <DiagnosticResult 
          title="Test Data Verification"
          result={diagnostics?.testData}
          icon={Activity}
        />
      </div>
      {/* Recommendations */}
      {overallStatus !== 'success' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">🔧 Recommendations:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            {!diagnostics?.databaseConnection?.success && (
              <li>• Check Supabase connection and authentication</li>
            )}
            {!diagnostics?.joseJimenezSearch?.success && (
              <li>• Verify Jose Jiminez test data exists in database</li>
            )}
            {!diagnostics?.searchFunctions?.success && (
              <li>• Test search functions with known good data</li>
            )}
            {!diagnostics?.testData?.success && (
              <li>• Ensure database contains valid person records</li>
            )}
            <li>• Check browser console for additional error details</li>
            <li>• Verify all database migrations have been applied</li>
          </ul>
        </div>
      )}
    </div>
  );
}